import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { CompanyData } from './excelProcessingService';

export interface PublishResult {
  messageId: string;
  company: CompanyData;
}

export interface PublishBatchRequest {
  companies: CompanyData[];
  userId: string;  // Add user ID to the request
}

export interface PublishBatchResponse {
  successful: number;
  failed: number;
  errors: string[];
}

/**
 * Service to publish companies to Pub/Sub via Firebase Cloud Function
 */
export class PubSubService {
  /**
   * Publish multiple companies to Pub/Sub via Firebase Cloud Function
   */
  static async publishCompanies(companies: CompanyData[], userId: string): Promise<{
    successful: PublishResult[];
    failed: { company: CompanyData; error: string }[];
  }> {
    try {
      // Call the Firebase Cloud Function to publish to Pub/Sub
      const publishBatch = httpsCallable<PublishBatchRequest, PublishBatchResponse>(
        functions,
        'publish_companies_to_pubsub'
      );

      const result = await publishBatch({ companies, userId });
      const response = result.data;

      // Convert response to expected format
      const successful: PublishResult[] = [];
      const failed: { company: CompanyData; error: string }[] = [];

      // Assume successful companies are the first N companies
      for (let i = 0; i < response.successful; i++) {
        if (companies[i]) {
          successful.push({
            messageId: `batch-${Date.now()}-${i}`,
            company: companies[i]
          });
        }
      }

      // Failed companies with their errors
      const failedStartIndex = response.successful;
      for (let i = 0; i < response.failed; i++) {
        const companyIndex = failedStartIndex + i;
        if (companies[companyIndex]) {
          failed.push({
            company: companies[companyIndex],
            error: response.errors[i] || 'Unknown error'
          });
        }
      }

      return { successful, failed };

    } catch (error) {
      // If the cloud function call fails, mark all companies as failed
      const failed = companies.map(company => ({
        company,
        error: `Failed to call publish function: ${error instanceof Error ? error.message : 'Unknown error'}`
      }));

      return {
        successful: [],
        failed
      };
    }
  }
}

/**
 * Factory function to create PubSubService
 */
export function createPubSubService(): PubSubService {
  return new PubSubService();
}